import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, refresh as refreshShopifyToken, clearTokens } from './shopifyAuth';

// Create an axios instance with default config
const api = axios.create({
  baseURL: (window as any).__ENV__?.VITE_API_URL || 'http://localhost:8080/',
  headers: {
    'Content-Type': 'application/json',
  }
});

// Flag to prevent multiple token refresh requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Request interceptor to attach the Shopify access token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Skip auth header for public endpoints (guest checkout, public inventory, stand sale form)
    if (
      config.url?.includes('/buylist/order/gjest') ||
      config.url?.includes('/inventory/featured') ||
      config.url?.includes('/contact/stand-sale') ||
      config.url?.includes('/inventory/')
    ) {
      return config;
    }

    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};
      const authHeader = token.trim().startsWith('Bearer ')
        ? token.trim()
        : `Bearer ${token.trim()}`;
      config.headers.Authorization = authHeader;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: refresh the Shopify token on 401, then retry once
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue requests that arrive while a refresh is in flight
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshShopifyToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return api(originalRequest);
        }
        // No refresh token / refresh failed → clear session and send to login
        processQueue(error, null);
        redirectToLogin();
        return Promise.reject(error);
      } catch (refreshError) {
        processQueue(refreshError, null);
        redirectToLogin();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

const redirectToLogin = () => {
  clearTokens();

  // Don't yank the user to login from checkout-related pages (guest flow keeps working)
  const currentPath = window.location.pathname;
  if (
    currentPath !== '/login' &&
    currentPath !== '/account/authorize' &&
    !currentPath.includes('/checkout') &&
    !currentPath.includes('/cart') &&
    !currentPath.includes('/buylist')
  ) {
    window.location.href = '/login';
  }
};

export default api;
